import { Trans } from '@lingui/macro'
import { Button, Divider, Stack, Typography, styled } from '@mui/material'
import { ApplicationTypes, MaterialSwatchMap, Model } from 'jms-domain'
import { useCallback, useState } from 'react'

import {
  AttributeListSelector,
  AttributeToggleSelector,
  MaterialSwatchSelector,
} from '@salsita-ai/platform-project-sdk/react'
import { PhxIcon, PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../systems/configurator-interface'
import Pulse from './animations/pulse'

const SwatchLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  nowrap: true,
  variant: 'caption',
  fontSize: 'inherit',
}))

const hasClickedButtonStorageKey = 'jms-hasClickedAddCabinetButton'

interface RoomContentProps {
  id: Model.Room['id']
  cabinetColor: Model.Room['cabinetColor']
  powderCoatType: Model.Room['powderCoatType']
}

export const RoomContent = (props: RoomContentProps) => {
  const { id, cabinetColor, powderCoatType } = props

  const [hasClickedAddCabinetButton, setHasClickedAddCabinetButton] = useState(
    sessionStorage.getItem(hasClickedButtonStorageKey) === 'true'
  )

  const { navigationSystem, storeSystem } = configuratorInterface.useDomain()
  const state = storeSystem.stateSlices.committed.value.state

  const handleAddCabinet = useCallback(() => {
    setHasClickedAddCabinetButton(true)
    sessionStorage.setItem(hasClickedButtonStorageKey, 'true')
    storeSystem.stageAndCommit([
      navigationSystem.getPushNavigationUpdate(ApplicationTypes.NavigationState.Recipes, state),
    ])
  }, [navigationSystem, storeSystem, state])

  return (
    <PropertyContainer.Group spacing="large">
      <PropertyContainer.Item title={<Trans>Cabinets</Trans>}>
        <Pulse active={!hasClickedAddCabinetButton} scaleFactor={1.04}>
          <Button
            startIcon={<PhxIcon name="PlusCircle" />}
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleAddCabinet}
            sx={{ width: '100%' }}
          >
            Add new cabinet
          </Button>
        </Pulse>
      </PropertyContainer.Item>

      <Divider sx={{ mt: 2, mb: -3 }} />

      <PropertyContainer.Item
        title={<Trans>Powder Coat Type</Trans>}
        description={<Trans>Select the powder coat type that will be applied to all cabinets</Trans>}
      >
        <AttributeListSelector entityId={id} instanceName="powderCoatType" />
      </PropertyContainer.Item>

      {powderCoatType !== Model.PowderCoatType.Anvil && (
        <PropertyContainer.Item
          title={
            <Stack direction="row" gap={1}>
              <Trans>Color</Trans>
              <SwatchLabel>{MaterialSwatchMap[cabinetColor]?.swatchName}</SwatchLabel>
            </Stack>
          }
          description={<Trans>Select the color that will be applied to all cabinets</Trans>}
        >
          <MaterialSwatchSelector entityId={id} instanceName="cabinetColor" />
        </PropertyContainer.Item>
      )}

      <PropertyContainer.Item title={<Trans>Fuel Type</Trans>}>
        <AttributeToggleSelector entityId={id} instanceName="fuelType" />
      </PropertyContainer.Item>
    </PropertyContainer.Group>
  )
}
